import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const PatientHealthInfo = ({ patientSymptoms, setPatientSymptoms, loading, handlePatientAISuggestion, patientSuggestion }) => (
  <div className="space-y-6">
    <Card className="p-6 bg-yellow-50 border-yellow-200">
      <h3 className="text-xl font-bold text-yellow-800 mb-3 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />Important Disclaimer
      </h3>
      <ul className="list-disc ml-5 text-gray-700 space-y-1 text-sm">
        <li><strong>This is NOT medical advice</strong> - Information provided is for educational purposes only</li>
        <li><strong>Always consult a licensed doctor</strong> for proper diagnosis and treatment</li>
        <li><strong>Emergency situations</strong> - Call emergency services immediately</li>
        <li><strong>Do not self-medicate</strong> without professional guidance</li>
      </ul>
    </Card>

    <Card className="p-6">
      <h2 className="text-2xl font-bold text-blue-600 mb-4">Describe Your Symptoms</h2>
      <p className="text-gray-600 mb-4">Get general health information about your symptoms. This is for informational purposes only.</p>
      <div className="space-y-4">
        <div>
          <Label htmlFor="patientSymptoms">What symptoms are you experiencing?</Label>
          <textarea id="patientSymptoms" value={patientSymptoms} onChange={(e) => setPatientSymptoms(e.target.value)} placeholder="Example: I have a fever, cough, and body aches for the past 2 days..." className="w-full px-3 py-2 border rounded-md" rows="4" />
        </div>
        <Button onClick={handlePatientAISuggestion} disabled={loading || !patientSymptoms.trim()} data-testid="get-health-info-btn" className="w-full bg-blue-600 hover:bg-blue-700">
          {loading ? <span className="flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Getting Information...</span> : 'Get Health Information'}
        </Button>
      </div>
    </Card>

    {patientSuggestion && (
      <div className="space-y-6">
        <Card className="p-6 bg-red-50 border-red-200"><p className="text-red-800 font-semibold">{patientSuggestion.disclaimer}</p></Card>

        {patientSuggestion.possible_conditions?.length > 0 && (
          <Card className="p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Possible Conditions (General Info)</h3>
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              {patientSuggestion.possible_conditions.map((c, i) => <li key={`cond-${i}`}>{c}</li>)}
            </ul>
          </Card>
        )}

        {patientSuggestion.general_care_suggestions?.length > 0 && (
          <Card className="p-6 bg-green-50 border-green-200">
            <h3 className="text-xl font-bold text-green-800 mb-3">General Care Suggestions</h3>
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              {patientSuggestion.general_care_suggestions.map((s, i) => <li key={`care-${i}`}>{s}</li>)}
            </ul>
          </Card>
        )}

        {patientSuggestion.when_to_see_doctor?.length > 0 && (
          <Card className="p-6 bg-red-50 border-red-200">
            <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />When to See a Doctor Immediately</h3>
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              {patientSuggestion.when_to_see_doctor.map((w, i) => <li key={`doc-${i}`} className="font-semibold">{w}</li>)}
            </ul>
          </Card>
        )}

        {patientSuggestion.otc_options?.length > 0 && (
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h3 className="text-xl font-bold text-blue-800 mb-3">Over-the-Counter Information</h3>
            <p className="text-sm text-gray-600 mb-2">General information only - Always read labels and consult a pharmacist</p>
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              {patientSuggestion.otc_options.map((o, i) => <li key={`otc-${i}`}>{o}</li>)}
            </ul>
          </Card>
        )}

        {patientSuggestion.preventive_measures?.length > 0 && (
          <Card className="p-6 bg-purple-50 border-purple-200">
            <h3 className="text-xl font-bold text-purple-800 mb-3">Preventive Health Tips</h3>
            <ul className="list-disc ml-5 text-gray-700 space-y-2">
              {patientSuggestion.preventive_measures.map((m, i) => <li key={`prev-${i}`}>{m}</li>)}
            </ul>
          </Card>
        )}

        <Card className="p-6 bg-gradient-to-r from-blue-500 to-purple-500 text-white">
          <h3 className="text-xl font-bold mb-2">Need Professional Medical Advice?</h3>
          <p className="mb-4">Book an appointment with a licensed doctor for proper diagnosis and treatment.</p>
          <Button onClick={() => window.location.href = '/appointments'} className="bg-white text-blue-600 hover:bg-gray-100" data-testid="book-appointment-btn">Book Appointment</Button>
        </Card>
      </div>
    )}
  </div>
);

export default PatientHealthInfo;
